import { useEffect, useState } from 'react';
import {
  getSystemProfile,
  getKeyUnmetNeeds,
  getSiteTreemap,
  getHospitalDistribution,
  getPhysicianDistribution,
} from '../../../utils/api';

const createInitialPriorityState = () => ({
  siteTreemapData: null,
  hospitalDistributionData: null,
  physicianDistributionData: null,
});

const createInitialSystemWideState = () => ({
  profile: null,
  keyNeeds: null,
});

const assertData = (condition, message) => {
  if (!condition) {
    throw new Error(message);
  }
};

const useSystemDetailData = (systemId, priorityUnmetNeed) => {
  const [systemWideData, setSystemWideData] = useState(createInitialSystemWideState);
  const [loading, setLoading] = useState(Boolean(systemId));
  const [priorityLoading, setPriorityLoading] = useState(false);
  const [error, setError] = useState(null);
  const [priorityData, setPriorityData] = useState(createInitialPriorityState);

  useEffect(() => {
    if (!systemId) {
      setLoading(false);
      setSystemWideData(createInitialSystemWideState());
      return;
    }

    let isMounted = true;

    const fetchSystemWideData = async () => {
      setLoading(true);
      setError(null);
      try {
        const [profileRes, needsRes] = await Promise.all([
          getSystemProfile(systemId),
          getKeyUnmetNeeds(systemId),
        ]);

        if (!isMounted) return;

        assertData(profileRes?.system, 'Invalid profile data received');
        assertData(
          needsRes?.key_unmet_needs && needsRes?.trends,
          'Invalid key unmet needs data received'
        );

        setSystemWideData({
          profile: profileRes,
          keyNeeds: needsRes,
        });
      } catch (err) {
        if (!isMounted) return;
        console.error('Error fetching system-wide data:', err);
        setError(err.message || 'Failed to load system-wide details.');
        setSystemWideData(createInitialSystemWideState());
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    fetchSystemWideData();

    return () => {
      isMounted = false;
    };
  }, [systemId]);

  useEffect(() => {
    if (!systemId || !priorityUnmetNeed) {
      setPriorityData(createInitialPriorityState());
      setPriorityLoading(false);
      return;
    }

    let isMounted = true;

    const fetchPriorityNeedData = async () => {
      setPriorityLoading(true);
      try {
        const [treemapRes, hospitalRes, physicianRes] = await Promise.all([
          getSiteTreemap(systemId, priorityUnmetNeed),
          getHospitalDistribution(systemId, priorityUnmetNeed),
          getPhysicianDistribution(systemId, priorityUnmetNeed),
        ]);

        if (!isMounted) return;

        assertData(treemapRes?.treemap, 'Invalid site treemap data received');
        assertData(
          hospitalRes?.bins && hospitalRes?.counts && hospitalRes?.averages,
          'Invalid hospital distribution data received'
        );
        assertData(
          physicianRes?.bins && physicianRes?.counts && physicianRes?.averages,
          'Invalid physician distribution data received'
        );

        setPriorityData({
          siteTreemapData: treemapRes.treemap,
          hospitalDistributionData: hospitalRes,
          physicianDistributionData: physicianRes,
        });
      } catch (err) {
        if (!isMounted) return;
        console.error('Error fetching priority-need specific data:', err);
        setPriorityData(createInitialPriorityState());
      } finally {
        if (isMounted) {
          setPriorityLoading(false);
        }
      }
    };

    fetchPriorityNeedData();

    return () => {
      isMounted = false;
    };
  }, [systemId, priorityUnmetNeed]);

  return {
    systemProfileData: systemWideData.profile,
    keyUnmetNeedsData: systemWideData.keyNeeds,
    ...priorityData,
    loading,
    priorityLoading,
    error,
  };
};

export default useSystemDetailData;
